/**
 * Yönetici Sınav Adayları — Spec Bölüm 7.4 + S3.3
 *
 *   /yonetici/sinavlar/:id/adaylar
 *
 * Listede:
 *  - Aday adı/anonim ID, pozisyon
 *  - Durum (IN_PROGRESS / COMPLETED / EXPIRED)
 *  - Skor + level + sertifika rozet
 *  - Sekme switch sayısı (uyarı)
 *  - "Detay" butonu → /yonetici/sinavlar/:id/aday/:attemptId
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

interface AttemptRow {
  id: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED' | 'ABANDONED';
  startedAt: string;
  completedAt: string | null;
  totalScore: number | null;
  scoreLevel: string | null;
  certificateId: string | null;
  tabSwitchCount: number;
  candidate: {
    id: string;
    fullName: string | null;
    email: string | null;
    anonymousId: string | null;
    position: string | null;
    experienceYears: string;
    displayName: string;
  };
}

interface AttemptsResponse {
  attempts: AttemptRow[];
  anonymousMode: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  IN_PROGRESS: 'Devam Ediyor',
  COMPLETED: 'Tamamlandı',
  EXPIRED: 'Süresi Doldu',
  ABANDONED: 'Terk Edildi',
};

const STATUS_COLORS: Record<string, string> = {
  IN_PROGRESS: 'bg-amber-50 text-amber-800',
  COMPLETED: 'bg-green-50 text-green-800',
  EXPIRED: 'bg-red-50 text-red-700',
  ABANDONED: 'bg-slate-100 text-slate-600',
};

const LEVEL_COLORS: Record<string, string> = {
  L0: 'bg-slate-100 text-slate-700',
  L1_MINUS: 'bg-orange-50 text-orange-800',
  L1: 'bg-yellow-50 text-yellow-800',
  L2: 'bg-sky-50 text-sky-800',
  L3: 'bg-green-50 text-green-800',
  L4: 'bg-purple-50 text-purple-800',
};

const EXP_LABELS: Record<string, string> = {
  LT_1Y: '<1 yıl',
  Y1_TO_2: '1-2 yıl',
  Y2_TO_5: '2-5 yıl',
  GT_5Y: '5+ yıl',
};

export function ExamAttemptsPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const params = useParams<{ id: string }>();
  const examId = params.id ?? '';
  const [data, setData] = useState<AttemptsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await api.get<AttemptsResponse>(
          `/api/manager/exams/${examId}/attempts`,
        );
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError('Adaylar yüklenemedi');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [examId, session, sessionPending, setLocation]);

  if (sessionPending || (!data && !error)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-5xl px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-5xl px-6 py-12">
          <Alert variant="danger">{error ?? 'Bilinmeyen hata'}</Alert>
        </main>
      </div>
    );
  }

  const completed = data.attempts.filter((a) => a.status === 'COMPLETED');
  const avgScore =
    completed.length > 0
      ? Math.round(
          completed.reduce((sum, a) => sum + (a.totalScore ?? 0), 0) /
            completed.length,
        )
      : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-12">
        <button
          type="button"
          onClick={() => setLocation(`/yonetici/sinavlar/${examId}`)}
          className="text-sm font-medium text-blue-900 hover:underline"
        >
          ← Sınav Detayı
        </button>
        <h1 className="mt-2 text-3xl font-bold text-slate-900">
          Adaylar ve Sonuçlar
        </h1>

        {data.anonymousMode && (
          <div className="mt-4">
            <Alert variant="info">
              <strong>Anonim mod:</strong> Aday ad ve e-postası gösterilmez,
              sadece takma adlarla görünür (Spec 7.3.3).
            </Alert>
          </div>
        )}

        {/* Özet metrikler */}
        <div className="mt-6 grid gap-4 sm:grid-cols-4">
          <Metric label="Toplam" value={String(data.attempts.length)} />
          <Metric label="Tamamlanan" value={String(completed.length)} />
          <Metric
            label="Devam"
            value={String(
              data.attempts.filter((a) => a.status === 'IN_PROGRESS').length,
            )}
          />
          <Metric
            label="Ekip Ortalaması"
            value={avgScore !== null ? `${avgScore}/100` : '—'}
          />
        </div>

        {/* Aday tablosu */}
        <Card className="mt-6">
          {data.attempts.length === 0 ? (
            <p className="text-center text-slate-500">
              Henüz aday yok. Erişim kodunu paylaştıktan sonra adaylar burada
              görünecek.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.attempts.map((a) => (
                <li key={a.id} className="py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          href={`/yonetici/sinavlar/${examId}/aday/${a.id}`}
                          className="text-base font-medium text-slate-900 hover:text-blue-900 hover:underline"
                        >
                          {a.candidate.displayName}
                        </Link>
                        {!data.anonymousMode && a.candidate.email && (
                          <span className="text-sm text-slate-500">
                            · {a.candidate.email}
                          </span>
                        )}
                      </div>
                      {a.candidate.position && (
                        <p className="mt-1 text-sm text-slate-600">
                          {a.candidate.position} ·{' '}
                          {EXP_LABELS[a.candidate.experienceYears] ??
                            a.candidate.experienceYears}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <span
                          className={`rounded-full px-2 py-0.5 ${
                            STATUS_COLORS[a.status] ?? 'bg-slate-100'
                          }`}
                        >
                          {STATUS_LABELS[a.status] ?? a.status}
                        </span>
                        {a.scoreLevel && (
                          <span
                            className={`rounded-full px-2 py-0.5 ${
                              LEVEL_COLORS[a.scoreLevel] ?? 'bg-slate-100'
                            }`}
                          >
                            {a.scoreLevel}
                          </span>
                        )}
                        {a.certificateId && (
                          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-900">
                            🏅 Sertifika
                          </span>
                        )}
                        {a.tabSwitchCount > 0 && (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-800">
                            ⚠️ {a.tabSwitchCount} sekme değiş.
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      {a.totalScore !== null && (
                        <p className="text-2xl font-bold text-blue-900">
                          {a.totalScore}
                          <span className="text-sm text-slate-400">/100</span>
                        </p>
                      )}
                      <p className="mt-1 text-xs text-slate-500">
                        {new Date(a.startedAt).toLocaleDateString('tr-TR')}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </main>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-center">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}
